import React, { createContext, useContext, useEffect, useState } from "react";

// ─── Shipping rates by country code (USD base, flat per order) ───────────────
// Adjust these numbers to whatever you want to charge
export const SHIPPING_RATES = {
  PK: { label: "Pakistan",      cost: 0,    currency: "PKR", note: "Free local delivery" },
  AE: { label: "UAE",           cost: 15,   currency: "AED", note: "Standard shipping" },
  US: { label: "United States", cost: 25,   currency: "USD", note: "International shipping" },
  GB: { label: "United Kingdom",cost: 20,   currency: "GBP", note: "International shipping" },
  SA: { label: "Saudi Arabia",  cost: 15,   currency: "SAR", note: "Standard shipping" },
  IN: { label: "India",         cost: 800,  currency: "INR", note: "Standard shipping" },
  CA: { label: "Canada",        cost: 28,   currency: "CAD", note: "International shipping" },
  AU: { label: "Australia",     cost: 30,   currency: "AUD", note: "International shipping" },
  DE: { label: "Germany",       cost: 22,   currency: "EUR", note: "International shipping" },
  FR: { label: "France",        cost: 22,   currency: "EUR", note: "International shipping" },
  DEFAULT: { label: "International", cost: 30, currency: "USD", note: "International shipping" },
};

// ─── Currency symbols ────────────────────────────────────────────────────────
export const CURRENCY_SYMBOLS = {
  PKR: "₨", AED: "AED", USD: "$", GBP: "£",
  SAR: "SR", INR: "₹", CAD: "CA$", AUD: "A$",
  EUR: "€", TRY: "₺", EGP: "£E", BDT: "৳",
};

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency]       = useState("PKR");   // default
  const [rates, setRates]             = useState({});       // all rates vs USD
  const [countryCode, setCountryCode] = useState("PK");
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const detect = async () => {
      try {
        // 1. Detect country via free IP geolocation (no key needed)
        const geoRes  = await fetch("https://ipapi.co/json/");
        const geoData = await geoRes.json();
        const cc      = geoData.country_code || "PK";
        setCountryCode(cc);

        // 2. Pick currency for that country
        const shipping = SHIPPING_RATES[cc] || SHIPPING_RATES.DEFAULT;
        const cur      = shipping.currency;
        setCurrency(cur);

        // 3. Fetch live rates from open.er-api.com (free, no key, 1500 req/month)
        const rateRes  = await fetch("https://open.er-api.com/v6/latest/PKR");
        const rateData = await rateRes.json();
        if (rateData.rates) setRates(rateData.rates);
      } catch (e) {
        // Silent fallback — stay PKR
        console.warn("Currency detection failed, defaulting to PKR", e);
      } finally {
        setLoading(false);
      }
    };
    detect();
  }, []);

  // Convert a PKR price to the user's local currency
  const convert = (pkrAmount) => {
    if (!pkrAmount || !rates[currency]) return pkrAmount;
    return Math.round(pkrAmount * rates[currency]);
  };

  // Format with symbol
  const format = (pkrAmount) => {
    const converted = convert(pkrAmount);
    const symbol    = CURRENCY_SYMBOLS[currency] || currency + " ";
    return `${symbol}${converted?.toLocaleString() ?? pkrAmount}`;
  };

  // Get shipping cost already in local currency
  const getShipping = () => {
    const rule = SHIPPING_RATES[countryCode] || SHIPPING_RATES.DEFAULT;
    // rule.cost is already in local currency (not PKR)
    if (rule.cost === 0) return { cost: 0, label: rule.note, display: "Free" };
    const sym = CURRENCY_SYMBOLS[rule.currency] || rule.currency + " ";
    return {
      cost:    rule.cost,
      label:   rule.note,
      display: `${sym}${rule.cost}`,
    };
  };

  return (
    <CurrencyContext.Provider value={{
      currency, countryCode, rates, loading,
      convert, format, getShipping,
      symbol: CURRENCY_SYMBOLS[currency] || currency,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}